import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useForm } from 'react-hook-form'
import RuleRiskLinksPanel from './components/RuleRiskLinksPanel'
import { PROCESSING_CATEGORY_LEGACY_FORM_OPTIONS } from '../../utils/processingCategory'
import {
  ChartBarIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

// Gaming-specific fields (include PascalCase keys returned by /api/gaming/sessions and engine)
const GAMING_FIELDS = [
  // Meter / session row (same names as DB/API)
  { id: 'Turnover', label: 'Turnover', type: 'number', category: 'Meter & bills' },
  { id: 'CreditWin', label: 'CreditWin', type: 'number', category: 'Meter & bills' },
  { id: 'Stroke', label: 'Stroke', type: 'number', category: 'Meter & bills' },
  { id: 'CashIn', label: 'CashIn', type: 'number', category: 'Meter & bills' },
  { id: 'CashOut', label: 'CashOut', type: 'number', category: 'Meter & bills' },
  { id: 'CashDrop', label: 'CashDrop', type: 'number', category: 'Meter & bills' },
  { id: 'Bill5', label: 'Bill5', type: 'number', category: 'Meter & bills' },
  { id: 'Bill10', label: 'Bill10', type: 'number', category: 'Meter & bills' },
  { id: 'Bill20', label: 'Bill20', type: 'number', category: 'Meter & bills' },
  { id: 'Bill50', label: 'Bill50', type: 'number', category: 'Meter & bills' },
  { id: 'Bill100', label: 'Bill100', type: 'number', category: 'Meter & bills' },
  { id: 'SessionPK', label: 'SessionPK', type: 'text', category: 'Meter & bills' },
  { id: 'EgmPK', label: 'EgmPK', type: 'text', category: 'Meter & bills' },
  { id: 'Venue', label: 'Venue', type: 'text', category: 'Meter & bills' },
  { id: 'StartTime', label: 'StartTime', type: 'text', category: 'Meter & bills' },
  { id: 'EndTime', label: 'EndTime', type: 'text', category: 'Meter & bills' },

  // Session Metrics
  { id: 'SessionDuration', label: 'Session Duration (minutes)', type: 'number', category: 'Session Metrics' },
  { id: 'TotalSpins', label: 'Total Spins', type: 'number', category: 'Session Metrics' },
  { id: 'AvgBetAmount', label: 'Average Bet Amount', type: 'number', category: 'Session Metrics' },
  { id: 'MaxBetAmount', label: 'Maximum Bet Amount', type: 'number', category: 'Session Metrics' },
  { id: 'TotalWagered', label: 'Total Wagered', type: 'number', category: 'Session Metrics' },
  { id: 'TotalWon', label: 'Total Won', type: 'number', category: 'Session Metrics' },
  { id: 'NetWinLoss', label: 'Net Win/Loss', type: 'number', category: 'Session Metrics' },
  
  // Game Information
  { id: 'GameID', label: 'Game ID', type: 'text', category: 'Game Information' },
  { id: 'GameType', label: 'Game Type', type: 'text', category: 'Game Information' },
  { id: 'GameCategory', label: 'Game Category', type: 'text', category: 'Game Information' },
  { id: 'GameRTP', label: 'Game RTP (%)', type: 'number', category: 'Game Information' },
  { id: 'GameVariance', label: 'Game Variance', type: 'text', category: 'Game Information' },
  
  // Device & Location
  { id: 'EGMID', label: 'EGM ID', type: 'text', category: 'Device & Location' },
  { id: 'GMID', label: 'GM ID', type: 'text', category: 'Device & Location' },
  { id: 'VenueID', label: 'Venue ID', type: 'text', category: 'Device & Location' },
  { id: 'DeviceType', label: 'Device Type', type: 'text', category: 'Device & Location' },
  { id: 'DeviceLocation', label: 'Device Location', type: 'text', category: 'Device & Location' },
  
  // Member Information
  { id: 'MemberID', label: 'Player ID', type: 'text', category: 'Member Information' },
  { id: 'MemberTier', label: 'Member Tier', type: 'text', category: 'Member Information' },
  { id: 'MemberStatus', label: 'Member Status', type: 'text', category: 'Member Information' },
  { id: 'ResponsibleGamingStatus', label: 'Responsible Gaming Status', type: 'text', category: 'Member Information' },
  
  // Time-based Analysis
  { id: 'SessionStartTime', label: 'Session Start Time', type: 'time', category: 'Time Analysis' },
  { id: 'SessionEndTime', label: 'Session End Time', type: 'time', category: 'Time Analysis' },
  { id: 'DayOfWeek', label: 'Day of Week', type: 'text', category: 'Time Analysis' },
  { id: 'TimeOfDay', label: 'Time of Day', type: 'text', category: 'Time Analysis' },
  { id: 'IsWeekend', label: 'Is Weekend', type: 'boolean', category: 'Time Analysis' },
  { id: 'IsHoliday', label: 'Is Holiday', type: 'boolean', category: 'Time Analysis' },
  
  // Behavioral Patterns
  { id: 'DeviceChanges', label: 'Device Changes Count', type: 'number', category: 'Behavioral Patterns' },
  { id: 'GameChanges', label: 'Game Changes Count', type: 'number', category: 'Behavioral Patterns' },
  { id: 'SessionsToday', label: 'Sessions Today', type: 'number', category: 'Behavioral Patterns' },
  { id: 'SessionsThisWeek', label: 'Sessions This Week', type: 'number', category: 'Behavioral Patterns' },
  { id: 'AvgSessionDuration', label: 'Avg Session Duration (7 days)', type: 'number', category: 'Behavioral Patterns' },
  
  // Risk Indicators
  { id: 'RapidPlayFlag', label: 'Rapid Play Flag', type: 'boolean', category: 'Risk Indicators' },
  { id: 'ExtendedPlayFlag', label: 'Extended Play Flag', type: 'boolean', category: 'Risk Indicators' },
  { id: 'ResponsibleGamingTrigger', label: 'RG Trigger', type: 'boolean', category: 'Risk Indicators' },
  { id: 'AnomalyScore', label: 'Anomaly Score', type: 'number', category: 'Risk Indicators' },

  // Period Comparison (engine-provided context)
  { id: 'periodComparison.current.sessionCount', label: 'Current Period Session Count', type: 'number', category: 'Period Comparison' },
  { id: 'periodComparison.previous.sessionCount', label: 'Previous Period Session Count', type: 'number', category: 'Period Comparison' },
  { id: 'periodComparison.current.avgTurnover', label: 'Current Period Avg Turnover', type: 'number', category: 'Period Comparison' },
  { id: 'periodComparison.previous.avgTurnover', label: 'Previous Period Avg Turnover', type: 'number', category: 'Period Comparison' },
  { id: 'periodComparison.changes.turnoverChange', label: 'Turnover Change (%)', type: 'number', category: 'Period Comparison' },
  { id: 'periodComparison.changes.winPercentageChange', label: 'Win % Change (pp)', type: 'number', category: 'Period Comparison' },
]

const OPERATORS = [
  { value: '>', label: 'Greater Than (>)' },
  { value: '>=', label: 'Greater Than or Equal (>=)' },
  { value: '<', label: 'Less Than (<)' },
  { value: '<=', label: 'Less Than or Equal (<=)' },
  { value: '=', label: 'Equals (=)' },
  { value: '!=', label: 'Not Equals (!=)' },
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'in', label: 'In List' },
  { value: 'between', label: 'Between' },
]

const GAME_TYPES = [
  'SLOTS', 'TABLE_GAMES', 'POKER', 'BLACKJACK', 'ROULETTE', 'BACCARAT', 'CRAPS'
]

function GamingRuleBuilder() {
  const { register, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      id: '',
      name: '',
      description: '',
      rationale: '',
      type: 'session',
      processing_category: 'session',
      severity: 'medium',
      // S6-F1: optional per-tier severity overrides.
      severity_by_tier: {},
      enabled: true,
      conditions: [{ field: '', operator: '>', value: '' }],
      actions: ['ALERT', 'RESPONSIBLE_GAMING_TRIGGER']
    }
  })

  const [conditions, setConditions] = useState([{ field: '', operator: '>', value: '' }])
  const [isTestingRule, setIsTestingRule] = useState(false)
  const [testResults, setTestResults] = useState(null)
  const [testMode, setTestMode] = useState('single')
  const [testDataText, setTestDataText] = useState('')
  const [bulkSize, setBulkSize] = useState(5)
  const location = useLocation()
  const [timeWindowDays, setTimeWindowDays] = useState(1)
  const processingCategory = watch('processing_category')

  // Group fields by category
  const groupedFields = GAMING_FIELDS.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = []
    acc[field.category].push(field)
    return acc
  }, {})

  // Add condition
  const addCondition = () => {
    setConditions([...conditions, { field: '', operator: '>', value: '' }])
  }

  // Remove condition
  const removeCondition = (index) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((_, i) => i !== index))
    }
  }

  // Update condition
  const updateCondition = (index, key, value) => {
    const newConditions = [...conditions]
    newConditions[index][key] = value
    setConditions(newConditions)
  }

  // S6: per-tier threshold overrides. Same semantics as the other rule
  // builders — writes condition.value_by_tier[tier] or drops the key when
  // all tiers are cleared.
  const [tierOpen, setTierOpen] = useState({})
  const [severityTierOpen, setSeverityTierOpen] = useState(false)
  const updateConditionTier = (index, tier, value) => {
    const newConditions = [...conditions]
    const c = { ...newConditions[index] }
    const byTier = { ...(c.value_by_tier || {}) }
    if (value === '' || value === null || value === undefined) delete byTier[tier]
    else byTier[tier] = Number(value)
    if (Object.keys(byTier).length === 0) delete c.value_by_tier
    else c.value_by_tier = byTier
    newConditions[index] = c
    setConditions(newConditions)
  }

  // Toggle expression mode
  const toggleExpressionMode = (index) => {
    const next = [...conditions]
    const c = next[index]
    if (c && c.expression !== undefined) next[index] = { field: '', operator: '>', value: '' }
    else next[index] = { expression: '' }
    setConditions(next)
  }

  const mapBackendToUiOperator = (op) => {
    switch (op) {
      case 'greater_than': return '>'
      case 'greater_than_or_equal': return '>='
      case 'less_than': return '<'
      case 'less_than_or_equal': return '<='
      case 'equals': return '='
      case 'not_equals': return '!='
      default: return op
    }
  }

  const mapUiToBackendOperator = (op) => {
    switch (op) {
      case '>': return 'greater_than'
      case '>=': return 'greater_than_or_equal'
      case '<': return 'less_than'
      case '<=': return 'less_than_or_equal'
      case '=': return 'equals'
      case '!=': return 'not_equals'
      default: return op
    }
  }

  // Prefill form when editing an existing rule by id
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const ruleId = params.get('id')
    if (!ruleId) return
    const load = async () => {
      try {
        const resp = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(ruleId)}`))
        if (!resp.ok) return
        const json = await resp.json()
        const r = json?.data
        if (!r) return
        reset()
        setValue('id', r.id || '')
        setValue('name', r.name || '')
        setValue('description', r.description || '')
        setValue('type', 'session')
        setValue('processing_category', (() => {
          const pc = String(r.processing_category || '').toLowerCase().replace(/_/g, '-')
          if (pc === 'cumulative') return 'cumulative'
          if (pc === 'interval' || pc === 'interval-based' || pc === 'transaction-based') return 'interval'
          return 'session'
        })())
        setValue('severity', (r.severity || 'medium').toLowerCase())
        // Normalize conditions array (DB often stores a top-level array of leaf conditions)
        let conds = []
        const c = r.conditions
        if (c != null && typeof c === 'object') {
          if (Array.isArray(c)) {
            conds = c
          } else if (c.type && Array.isArray(c.conditions)) {
            conds = c.conditions
          } else {
            conds = [c]
          }
        }
        const normalized = conds.map((ci) => {
          if (ci && ci.expression !== undefined) {
            return { expression: String(ci.expression) }
          }
          return {
            field: ci?.field || '',
            operator: mapBackendToUiOperator(ci?.operator || '>'),
            value: ci?.value !== undefined ? ci.value : ''
          }
        })
        setConditions(normalized.length ? normalized : [{ field: '', operator: '>', value: '' }])
      } catch (e) {
        // ignore errors
      }
    }
    load()
  }, [location.search, reset, setValue])

  // Test rule using backend
  const testRule = async () => {
    setIsTestingRule(true)
    setTestResults(null)
    try {
      const id = (watch('id') || '').trim()
      if (!id) { alert('Please provide Rule ID before testing'); return }
      let payload = { testType: testMode }
      if (testMode === 'custom') {
        try { payload.data = JSON.parse(testDataText || '[]') } catch (e) { alert('Invalid JSON'); return }
      }
      if (testMode === 'single') {
        try { payload.data = JSON.parse(testDataText || '{}') } catch (e) {
          const c = conditions[0] || {}; payload.data = c.field ? { [c.field]: c.value } : {}
        }
      }
      if (testMode === 'bulk') payload.batchSize = Math.max(1, Number(bulkSize) || 5)
      const url = getLocalApiUrl(`/api/rules/${encodeURIComponent(id)}/test`)
      const resp = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      const json = await resp.json()
      if (!json.success) { alert('Rule test failed: ' + (json.error || 'Unknown error')); return }
      setTestResults(json.data)
    } catch (e) { alert('Error testing rule: ' + e.message) } finally { setIsTestingRule(false) }
  }

  const onSubmit = async (data) => {
    // S3: policy rationale is required before a rule can be saved.
    const rationale = String(data.rationale || '').trim()
    if (rationale.length < 20) {
      alert('Please provide a rationale of at least 20 characters explaining why this rule exists and what risk it addresses. This is required by SVCP §5.3.')
      return
    }
    data.rationale = rationale
    const out = conditions.map(c => {
      if (c.expression !== undefined) return { expression: String(c.expression) }
      const row = {
        field: c.field,
        operator: mapUiToBackendOperator(c.operator),
        value: c.value,
      }
      // S6: carry per-tier overrides through to the payload.
      if (c.value_by_tier && typeof c.value_by_tier === 'object' && Object.keys(c.value_by_tier).length > 0) {
        row.value_by_tier = c.value_by_tier
      }
      return row
    })
    data.conditions = out.length > 1 ? { type: 'and', conditions: out } : out[0]
    if (processingCategory === 'cumulative') {
      const days = Number(timeWindowDays) || 1
      data.time_window_days = days
      data.aggregation = Array.from(new Set([...(data.aggregation || []), `${days}d`]))
    }
    // Normalize processing category to backend format
    const payload = {
      ...data,
      processing_category:
        data.processing_category === 'session'
          ? 'session-based'
          : data.processing_category === 'interval'
            ? 'interval-based'
            : data.processing_category,
      type: 'session',
    }
    // S6-F1: strip empty severity_by_tier so rules serialize identically
    // to pre-S6-F1 rules when no overrides authored.
    if (
      payload.severity_by_tier &&
      typeof payload.severity_by_tier === 'object' &&
      Object.keys(payload.severity_by_tier).length === 0
    ) {
      delete payload.severity_by_tier
    }
    try {
      const method = data.id ? 'PUT' : 'POST'
      const url = data.id ? getLocalApiUrl(`/api/rules/${encodeURIComponent(data.id)}`) : getLocalApiUrl('/api/rules')
      const resp = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      const json = await resp.json()
      if (!json.success) {
        alert('Failed to save rule: ' + (json.error || 'Unknown error'))
      } else {
        alert('Rule saved')
      }
    } catch (e) {
      alert('Failed to save rule: ' + e.message)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link 
                to="/monitor-safe/builder"
                className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
              </Link>
              <ChartBarIcon className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gaming Rule Builder</h1>
                <p className="text-gray-600">Create rules for monitoring gaming sessions and player behavior</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={testRule}
                disabled={isTestingRule}
                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <BeakerIcon className="w-4 h-4 mr-2" />
                {isTestingRule ? 'Testing...' : 'Test Rule'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Rule Configuration */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rule ID</label>
                    <input
                      {...register('id', { required: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="SES_001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                    <input
                      {...register('name', { required: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Extended Gaming Session"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Detects gaming sessions that exceed normal duration or show concerning patterns"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Rationale <span className="text-red-500">*</span>
                        <span className="text-gray-400 font-normal ml-1 text-xs">
                          (why this rule exists, what risk it addresses, evidence behind thresholds)
                        </span>
                      </label>
                      <span className={`text-xs font-mono ${(watch('rationale') || '').trim().length >= 20 ? 'text-green-600' : 'text-gray-400'}`}>
                        {(watch('rationale') || '').trim().length} / 20 min
                      </span>
                    </div>
                    <textarea
                      {...register('rationale')}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder={'e.g. "Problem-gambling harm mitigation per RG-2026-012. Trigger at 4h continuous play reflects board-approved intervention policy; evidence: internal harm-minimisation review Q3 2025."'}
                    />
                    {(watch('rationale') || '').trim().length > 0 && (watch('rationale') || '').trim().length < 20 && (
                      <p className="mt-1 text-xs text-red-600">
                        Minimum 20 characters required before this rule can be submitted.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Evaluation grain</label>
                    <select
                      {...register('processing_category')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      {PROCESSING_CATEGORY_LEGACY_FORM_OPTIONS.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {processingCategory === 'cumulative' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cumulative Time Window</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Presets</label>
                          <select
                            value={String(timeWindowDays)}
                            onChange={(e) => setTimeWindowDays(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          >
                            {[1,2,7,15,30,90].map((d) => (
                              <option key={d} value={d}>{d} day{d>1?'s':''}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Or enter days</label>
                          <input
                            type="number"
                            min={1}
                            value={timeWindowDays}
                            onChange={(e) => setTimeWindowDays(Math.max(1, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="e.g., 45"
                          />
                        </div>
                        <div className="flex items-end">
                          <div className="text-sm text-gray-600">Evaluates metrics over the last <span className="font-medium">{timeWindowDays}</span> day{timeWindowDays>1?'s':''}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                    <select
                      {...register('severity')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    {/* S6-F1: opt-in per-tier severity */}
                    {(() => {
                      const byTier = watch('severity_by_tier') || {}
                      const hasOverrides = Object.keys(byTier).length > 0
                      const panelOpen = severityTierOpen || hasOverrides
                      return (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                              onClick={() => setSeverityTierOpen(v => !panelOpen)}
                            >
                              {panelOpen ? '− Hide per-tier severity' : '+ Per-tier severity (S6-F1)'}
                            </button>
                            {hasOverrides && !severityTierOpen && (
                              <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                Tier severity active
                              </span>
                            )}
                          </div>
                          {panelOpen && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                              {['simplified','standard','enhanced','prohibited'].map(tier => (
                                <div key={tier}>
                                  <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">{tier}</label>
                                  <select
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    value={byTier[tier] || ''}
                                    onChange={(e) => {
                                      const next = { ...(byTier || {}) }
                                      if (!e.target.value) delete next[tier]
                                      else next[tier] = e.target.value
                                      setValue('severity_by_tier', next, { shouldDirty: true })
                                    }}
                                  >
                                    <option value="">(base)</option>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                  </select>
                                </div>
                              ))}
                              <p className="col-span-2 md:col-span-4 text-[10px] text-gray-500 italic">
                                When a patron's CDD tier (S2) matches, the tier severity replaces the base on the published alert. Leave as (base) to fall back.
                              </p>
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>

              {/* Linked Risks — S10 */}
              <div className="bg-white rounded-lg shadow p-6">
                <RuleRiskLinksPanel ruleId={watch('id') || ''} />
              </div>

              {/* Conditions */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Rule Conditions</h3>
                  <button
                    type="button"
                    onClick={addCondition}
                    className="flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Add Condition
                  </button>
                </div>

                <div className="space-y-4">
                  {conditions.map((condition, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs text-gray-600">Condition {index + 1}</div>
                        <div className="flex items-center space-x-2">
                          <label className="flex items-center text-xs text-gray-600">
                            <input type="checkbox" checked={condition.expression !== undefined} onChange={() => toggleExpressionMode(index)} className="mr-2" />
                            Use expression
                          </label>
                          {conditions.length > 1 && (
                            <button type="button" onClick={() => removeCondition(index)} className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors" title="Remove condition">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {condition.expression !== undefined ? (
                        <input
                          type="text"
                          value={condition.expression}
                          onChange={(e) => updateCondition(index, 'expression', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          placeholder="e.g., (${SessionDuration} >= 240 && ${AnomalyScore} >= 70) || (${DeviceChanges} >= 3)"
                        />
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <select
                              value={condition.field}
                              onChange={(e) => updateCondition(index, 'field', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            >
                              <option value="">Select Field</option>
                              {condition.field &&
                                !GAMING_FIELDS.some((f) => f.id === condition.field) && (
                                  <option value={condition.field}>{condition.field}</option>
                                )}
                              {Object.entries(groupedFields).map(([category, fields]) => (
                                <optgroup key={category} label={category}>
                                  {fields.map((field) => (
                                    <option key={field.id} value={field.id}>
                                      {field.label}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                          <div>
                            <select
                              value={condition.operator}
                              onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            >
                              {OPERATORS.map((op) => (
                                <option key={op.value} value={op.value}>
                                  {op.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <input
                              type="text"
                              value={condition.value}
                              onChange={(e) => updateCondition(index, 'value', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              placeholder="Value"
                            />
                          </div>
                        </div>
                      )}
                      {/* S6: per-tier thresholds — numeric operators only */}
                      {condition.expression === undefined && ['>','>=','<','<=','between','greater_than','greater_than_or_equal','less_than','less_than_or_equal'].includes(condition.operator) && (() => {
                        const hasOverrides = condition.value_by_tier && Object.keys(condition.value_by_tier).length > 0
                        const tierPanelOpen = !!tierOpen[index] || hasOverrides
                        return (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                                onClick={() => setTierOpen(prev => ({ ...prev, [index]: !tierPanelOpen }))}
                              >
                                {tierPanelOpen ? '− Hide per-tier thresholds' : '+ Per-tier thresholds (S6)'}
                              </button>
                              {hasOverrides && !tierOpen[index] && (
                                <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                  Tier overrides active
                                </span>
                              )}
                            </div>
                            {tierPanelOpen && (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                                {['simplified','standard','enhanced','prohibited'].map(tier => (
                                  <div key={tier}>
                                    <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">{tier}</label>
                                    <input
                                      type="number"
                                      step="any"
                                      placeholder={condition.value !== '' && condition.value != null ? String(condition.value) : '(base)'}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                      value={condition.value_by_tier?.[tier] ?? ''}
                                      onChange={(e) => updateConditionTier(index, tier, e.target.value)}
                                    />
                                  </div>
                                ))}
                                <p className="col-span-2 md:col-span-4 text-[10px] text-gray-500 italic">
                                  When a patron's CDD tier (S2) matches, the tier value is used instead of the base value. Leave blank to fall back to the base.
                                </p>
                              </div>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                  Create Gaming Rule
                </button>
              </div>
            </form>
          </div>

          {/* Test Results Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Results</h3>
              {/* Test Controls */}
              <div className="space-y-3 mb-4">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <select className="border rounded px-2 py-1" value={testMode} onChange={(e)=>setTestMode(e.target.value)}>
                    <option value="single">Single</option>
                    <option value="custom">Custom Dataset</option>
                    <option value="bulk">Bulk (generated)</option>
                  </select>
                  {testMode === 'bulk' && (
                    <input className="border rounded px-2 py-1" type="number" min={1} value={bulkSize} onChange={(e)=>setBulkSize(e.target.value)} placeholder="Batch size" />
                  )}
                </div>
                {(testMode === 'single' || testMode === 'custom') && (
                  <textarea className="w-full border rounded px-2 py-1 text-xs font-mono" rows={6} value={testDataText} onChange={(e)=>setTestDataText(e.target.value)} placeholder={testMode==='single' ? '{"MemberID":"M1","SessionDuration":300}' : '[{"MemberID":"M1","SessionDuration":300},{"MemberID":"M1","SessionDuration":400}]'} />
                )}
              </div>

              {!testResults ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 text-sm">Prepare test inputs above and click "Test Rule"</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-700">{testResults.ruleName} ({testResults.ruleId})</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>Records: <span className="font-medium">{testResults.summary?.totalRecords}</span></div>
                    <div>Triggered: <span className="font-medium">{testResults.summary?.triggeredCount}</span></div>
                    <div>Time: <span className="font-medium">{testResults.summary?.processingTime}ms</span></div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-auto">
                    {(testResults.results || []).map((r, idx) => (
                      <div key={idx} className={`p-2 rounded border text-xs ${r.triggered ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-mono truncate">{JSON.stringify(r.input)}</div>
                          <div className={`ml-2 ${r.triggered ? 'text-green-700' : 'text-gray-500'}`}>{r.triggered ? 'Triggered' : 'No'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GamingRuleBuilder